import moment from 'moment';
import React from 'react';
import { Spinner } from 'react-bootstrap';
import { BiRefresh } from 'react-icons/bi';
import { FaCheck, FaEnvelope, FaExclamationTriangle, FaFileAlt, FaInfoCircle } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import { KTSVG } from '../../..//_metronic/helpers';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import ExpenseStatusComp from '../../../components/StatusComp/ExpenseStatusComp';
import { constraintConfig } from '../../../constraintConfig';
import labelKey from '../../localization/label.json';
import { canEditLineItem, canMergeLineItem, canUnmergeLineItem, canViewViolations, getEffectiveLineItemId } from './allReceiptsActionHelpers';
import { formatReceiptNumberDisplay } from '../expenseItems/MergeSelectedLineItemHeader';

interface InvoiceGridListProps {
    data: any[];
    isLoading: boolean;
    selectedInvoiceId: number | null;
    onRowClick: (item: any) => void;
    onDelete: (id: number) => void;
    onRetry: (id: number) => void;
    onViewFile: (fileURL: string) => void;
    heicPreviewUrls: { [key: string]: string };
    showUserDetails?: boolean;
    isDetailPanelOpen?: boolean;
    onViewReport?: (item: any) => void;
    onViewLineItem?: (expenseItemId: number) => void;
    onCompareOcr?: (item: any) => void;
    onViewOcrText?: (item: any) => void;
    onCopyAttachmentUrl?: (item: any) => void;
    onViewViolations?: (item: any) => void;
    onMergeLineItem?: (item: any) => void;
    onUnmergeLineItem?: (item: any) => void;
    onEditLineItem?: (item: any) => void;
}

const isMsgOrEml = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lowerURL = fileURL.toLowerCase();
    return lowerURL.endsWith('.msg') || lowerURL.endsWith('.eml');
};

const isHeicFile = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lowerURL = fileURL.toLowerCase();
    return lowerURL.endsWith('.heic') || lowerURL.endsWith('.heif');
};

const getFileExtension = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const ext = fileURL.toLowerCase().split('.').pop();
    return ext?.toUpperCase() || 'File';
};

const getFileNameFromURL = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const parts = fileURL.split('/');
    return parts[parts.length - 1] || 'File';
};

const SourceIcon = ({ recordSourceId }: { recordSourceId?: number }) => {
    if (recordSourceId === constraintConfig.recordSourceId.WebApp) {
        return <KTSVG path="/media/icons/figmaIcons/Web.svg" className='svg-icon-1 ss-source-icon' data-tooltip-id="web-source" data-tooltip-content='Website' />;
    }
    if (recordSourceId === constraintConfig.recordSourceId.Mobile) {
        return <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className='svg-icon-1 ss-source-icon' data-tooltip-id="mobile-source" data-tooltip-content='Mobile' />;
    }
    if (recordSourceId === constraintConfig.recordSourceId.BankFeed) {
        return <KTSVG path="/media/icons/figmaIcons/Bank.svg" className='svg-icon-1 ss-source-icon' data-tooltip-id="bank-source" data-tooltip-content='Bank' />;
    }
    if (recordSourceId === constraintConfig.recordSourceId.GorillaExpense) {
        return <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className='svg-icon-1' data-tooltip-id="gorilla-source" data-tooltip-content='Gorilla Expense' />;
    }
    return <span className="text-muted">N/A</span>;
};

const InvoiceGridList: React.FC<InvoiceGridListProps> = ({
    data,
    isLoading,
    selectedInvoiceId,
    onRowClick,
    onDelete,
    onRetry,
    onViewFile,
    heicPreviewUrls,
    showUserDetails = true,
    isDetailPanelOpen = false,
    onViewReport,
    onViewLineItem,
    onCompareOcr,
    onViewOcrText,
    onCopyAttachmentUrl,
    onViewViolations,
    onMergeLineItem,
    onUnmergeLineItem,
    onEditLineItem,
}) => {
    if (isLoading) {
        return <LoadingComponent />;
    }

    if (!data || data.length === 0) {
        return <NoRecordFound />;
    }

    const colClass = isDetailPanelOpen
        ? 'col-12 col-md-6 col-xxl-6'
        : 'col-12 col-sm-6 col-lg-6 col-xl-4 col-xxl-3';

    return (
        <div className="row ss-all-receipts-grid g-3 m-0">
            <Tooltip id="all-receipts-grid-actions" place="top" />
            {data.map((item: any, index: number) => {
                const isMsgOrEmlFile = isMsgOrEml(item.fileURL);
                const isHeicFileCheck = isHeicFile(item.fileURL);
                const fileName = getFileNameFromURL(item.fileURL);
                const heicPreviewUrl = heicPreviewUrls[item.invoiceDataDraftId];
                const lineItemId = getEffectiveLineItemId(item);
                const isSelected = selectedInvoiceId === item.invoiceDataDraftId;
                const showFooterActions =
                    item?.ocrReadStatus === 2 ||
                    item?.ocrReadStatus === 3 ||
                    item?.ocrReadStatus === 4 ||
                    item?.ocrReadStatus === 5 ||
                    canViewViolations(item) ||
                    canMergeLineItem(item) ||
                    canUnmergeLineItem(item) ||
                    canEditLineItem(item) ||
                    !!item?.fileURL;

                return (
                    <div className={colClass} key={item.invoiceDataDraftId || index}>
                        <div
                            className={`card cursor-pointer h-100 ss-receipt-grid-card ${isSelected ? 'ss-receipt-grid-card--selected' : ''}`}
                            style={{
                                border: isSelected ? '2px solid #009ef7' : undefined,
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                            }}
                            onClick={() => onRowClick(item)}
                        >
                            <div className='ss-receipt-grid-card__preview position-relative'>
                                <div className='position-absolute m-2 z-index-1' style={{ left: 0, bottom: 0 }}>
                                    {item.ocrReadStatus === 0 ?
                                        <span className="btn btn-sm btn-primary fs-7 fw-semibold w-max-content shadow-sm">{labelKey.processing}... <Spinner animation="border" variant="white" size="sm" /> </span>
                                        : item.ocrReadStatus === 1 ?
                                            <span className="btn btn-sm btn-success fs-7 fw-semibold w-max-content shadow-sm">{labelKey.readyToUse} <FaCheck className='ms-1' /> </span>
                                            : item.ocrReadStatus === 2 ?
                                                <span className="btn btn-sm btn-danger fs-7 fw-semibold w-max-content shadow-sm">{labelKey.failed} <FaExclamationTriangle className='ms-1' /> </span>
                                                : item.ocrReadStatus === 3 ?
                                                    <span className="btn btn-sm btn-secondary fs-7 fw-bold w-max-content shadow-sm">{labelKey.attached}</span>
                                                    : item.ocrReadStatus === 4 ?
                                                        <span className="btn btn-sm btn-success fs-7 fw-bold w-max-content shadow-sm">{labelKey.submitted}</span>
                                                        : item.ocrReadStatus === 5 ?
                                                            <span className="btn btn-sm btn-warning fs-7 fw-bold w-max-content shadow-sm">{labelKey.alreadyUsed} </span>
                                                            : item.ocrReadStatus === 6 ?
                                                                <span className="btn btn-sm btn-info fs-7 fw-bold w-max-content shadow-sm">{labelKey.futureReceipt} </span>
                                                                : item.ocrReadStatus === 7 ?
                                                                    <span className="btn btn-sm btn-info fs-7 fw-bold w-max-content shadow-sm">{labelKey.cCReceipt} </span>
                                                                    : null
                                    }
                                </div>

                                {item?.expenseItemId === null || (item.ocrReadStatus !== 3 && item.ocrReadStatus !== 4) ? (
                                    <div
                                        className='btn btn-icon btn-bg-light hover-danger btn-sm position-absolute m-2 shadow-sm rounded-circle'
                                        style={{ left: 0, zIndex: 12, top: 0 }}
                                        onClick={(e) => {
                                             e.stopPropagation();
                                             onDelete(item?.invoiceDataDraftId);
                                        }}
                                    >
                                        <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-hover-danger' svgClassName='svg-danger' />
                                    </div>
                                ) : null}

                                {item?.noOfRetry <= 2 && item?.ocrReadStatus === 2 && (
                                    <div
                                        data-tooltip-id="all-receipts-grid-actions"
                                        data-tooltip-content='Retry Invoice'
                                        className='btn btn-icon btn-bg-warning btn-sm position-absolute m-2 shadow-sm rounded-circle'
                                        style={{ right: 0, zIndex: 12, top: 0 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRetry(item?.invoiceDataDraftId);
                                        }}
                                    >
                                        <BiRefresh className='h-15px w-15px' />
                                    </div>
                                )}

                                {item.fileURL?.endsWith('.pdf') ? (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        style={{ cursor: 'pointer', position: 'relative', width: '100%', height: '100%' }}
                                    >
                                        <embed
                                            src={`${item.fileURL}#toolbar=0&navpanes=0&scrollbar=0`}
                                            type="application/pdf"
                                            width="100%"
                                            height="100%"
                                            className="cursor-pointer"
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                        <div style={{ position: 'absolute', top: 0, left: 0, width: '96%', height: '100%', backgroundColor: 'transparent' }} />
                                    </div>
                                ) : isMsgOrEmlFile ? (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        style={{ cursor: 'pointer', position: 'relative', width: '100%', height: '100%' }}
                                    >
                                        <div className="d-flex flex-column align-items-center justify-content-center p-2 w-100 h-100 ss-receipt-preview-placeholder" style={{ backgroundColor: '#f8f9fa', overflow: 'hidden' }}>
                                            <FaEnvelope className="text-primary" style={{ fontSize: '42px', flexShrink: 0 }} />
                                            <h6 className="mt-2 text-center px-2 w-100 fs-7" style={{ wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{fileName}</h6>
                                            <span className="badge bg-primary mt-1">{getFileExtension(item.fileURL)}</span>
                                            <p className="text-muted mt-1 mb-0"><small>Click to view</small></p>
                                        </div>
                                    </div>
                                ) : isHeicFileCheck ? (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        style={{ cursor: 'pointer', position: 'relative', width: '100%', height: '100%' }}
                                    >
                                        {heicPreviewUrl ? (
                                            <img className="card-img-top w-100 cursor-pointer" src={heicPreviewUrl} alt="HEIC preview" style={{ objectFit: 'contain', width: '100%', height: '100%' }} />
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center p-2 w-100 h-100 ss-receipt-preview-placeholder" style={{ backgroundColor: '#f8f9fa' }}>
                                                <FaFileAlt className="text-warning" style={{ fontSize: '42px' }} />
                                                <h6 className="mt-2 text-center px-2 w-100 fs-7">{fileName}</h6>
                                                <span className="badge bg-warning mt-1">{getFileExtension(item.fileURL)}</span>
                                                <p className="text-muted mt-1 mb-0"><small>Converting preview...</small></p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <img
                                        className="card-img-top w-100 cursor-pointer"
                                        src={item.fileURL}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = 'media/svg/files/blank-image.svg';
                                        }}
                                        alt="Receipt"
                                        style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                                    />
                                )}
                            </div>

                            <div className="p-3 card-body-content">
                                {/* User & Header Meta */}
                                <div className="ss-receipt-card-header-meta mb-2">
                                    {showUserDetails && (
                                        <div className='d-flex align-items-center justify-content-between gap-2 mb-2'>
                                            <div className='d-flex align-items-center gap-2 min-w-0'>
                                                <div className='ss-receipt-user-avatar'>
                                                    <i className='fa fa-user fs-8 text-primary' />
                                                </div>
                                                <div className='min-w-0'>
                                                    <span className='fw-bold text-gray-900 fs-7 text-truncate d-inline-block' title={item.orgUserName || ''}>
                                                        {item.orgUserName || 'N/A'}
                                                    </span>
                                                    <span className='badge badge-light-primary ms-1 fs-8 py-0 px-1'>ID: {item.orgUserId}</span>
                                                </div>
                                            </div>
                                            {(item?.recordSourceID != null || item?.recordSourceId != null) && (
                                                <SourceIcon recordSourceId={item.recordSourceID ?? item.recordSourceId} />
                                            )}
                                        </div>
                                    )}

                                    {(item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4) && (
                                        <div className='ss-receipt-card-report-box p-2 mb-2 rounded bg-light-info border border-info border-opacity-25'>
                                            <div className='row g-2 align-items-center'>
                                                <div className='col-6'>
                                                    <span className='ss-receipt-card-field__label text-info'>{labelKey.reportId}</span>
                                                    <div className='ss-receipt-card-field__value'>
                                                        {item.trackingId ? (
                                                            <span
                                                                className='text-primary fw-bold cursor-pointer text-decoration-underline'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onViewReport?.(item);
                                                                }}
                                                            >
                                                                {item.trackingId}
                                                            </span>
                                                        ) : 'N/A'}
                                                    </div>
                                                </div>
                                                <div className='col-6 text-end'>
                                                    {item?.expenseStatus?.id > 0 ? (
                                                        <ExpenseStatusComp
                                                            expenseStatusId={item?.expenseStatus?.id}
                                                            expenseStatusTitle={item?.expenseStatus?.title}
                                                        />
                                                    ) : <span className="text-muted fs-8">N/A</span>}
                                                </div>
                                                {item.reportTitle && (
                                                    <div className='col-12 mt-1'>
                                                        <span className='ss-receipt-card-field__label text-info'>{labelKey.reportTitle}</span>
                                                        <div className='ss-receipt-card-field__value text-gray-800' title={item.reportTitle}>
                                                            {item.reportTitle}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Item Details */}
                                {item.ocrReadStatus !== 5 && (
                                    <div className="ss-receipt-card-section mb-2">
                                        <div className="ss-receipt-card-section__header">
                                            <i className="fa fa-receipt me-1 text-primary"></i>
                                            <span>Item Details</span>
                                        </div>
                                        <div className='row g-2'>
                                            <div className='col-6'>
                                                <div className='ss-receipt-card-field'>
                                                    <span className='ss-receipt-card-field__label'>{labelKey.paymentMethod}</span>
                                                    <span className='ss-receipt-card-field__value'>
                                                        {item?.expensePayment?.id > 0 ? item.expensePayment?.title : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='col-6'>
                                                <div className='ss-receipt-card-field'>
                                                    <span className='ss-receipt-card-field__label'>Category</span>
                                                    <span className='ss-receipt-card-field__value'>
                                                        {item.category && lineItemId ? (
                                                            <span
                                                                className='text-primary fw-bold cursor-pointer text-decoration-underline'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onViewLineItem?.(lineItemId);
                                                                }}
                                                            >
                                                                {item.category}
                                                            </span>
                                                        ) : (item.category || 'N/A')}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='col-6'>
                                                <div className='ss-receipt-card-field'>
                                                    <span className='ss-receipt-card-field__label'>{labelKey.vendor}</span>
                                                    <span className='ss-receipt-card-field__value'>
                                                        {item.itemVendorName || item.vendorName || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='col-6'>
                                                <div className='ss-receipt-card-field'>
                                                    <span className='ss-receipt-card-field__label'>{labelKey.invoiceNo}</span>
                                                    <span
                                                        className='ss-receipt-card-field__value'
                                                        title={
                                                            item.invoiceNo &&
                                                            String(item.invoiceNo) !==
                                                                formatReceiptNumberDisplay(item.invoiceNo)
                                                                ? String(item.invoiceNo)
                                                                : undefined
                                                        }
                                                    >
                                                        {item.invoiceNo
                                                            ? formatReceiptNumberDisplay(item.invoiceNo)
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='col-6'>
                                                <div className='ss-receipt-card-field'>
                                                    <span className='ss-receipt-card-field__label'>Item Date</span>
                                                    <span className='ss-receipt-card-field__value'>
                                                        {item.expenseItemDate ? moment(item.expenseItemDate).format('MM-DD-YYYY') : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className='col-6'>
                                                <div className='ss-receipt-card-field'>
                                                    <span className='ss-receipt-card-field__label'>{labelKey.amount}</span>
                                                    <span className='ss-receipt-card-field__value fw-bold text-success'>
                                                        <CurrencyComp amount={item.expenseItemAmount ?? item.amount} />
                                                    </span>
                                                </div>
                                            </div>
                                            {item.lineItemRecordSourceId && (
                                                <div className='col-12'>
                                                    <div className='ss-receipt-card-field d-flex flex-row align-items-center gap-1'>
                                                        <span className='ss-receipt-card-field__label mb-0'>Source:</span>
                                                        <SourceIcon recordSourceId={item.lineItemRecordSourceId} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Receipt Details */}
                                <div className="ss-receipt-card-section mb-2">
                                    <div className="ss-receipt-card-section__header">
                                        <i className="fa fa-file-invoice me-1 text-primary"></i>
                                        <span>Receipt Details</span>
                                    </div>
                                    <div className='row g-2'>
                                        <div className='col-6'>
                                            <div className='ss-receipt-card-field'>
                                                <span className='ss-receipt-card-field__label'>Receipt Date</span>
                                                <span className='ss-receipt-card-field__value'>
                                                    {item.ocrReadStatus !== 0 && item.invoiceDate
                                                        ? moment(item.invoiceDate).format('MM-DD-YYYY hh:mm A')
                                                        : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className='col-6'>
                                            <div className='ss-receipt-card-field'>
                                                <span className='ss-receipt-card-field__label'>{labelKey.amount}</span>
                                                <span className='ss-receipt-card-field__value fw-bold text-primary'>
                                                    <CurrencyComp amount={item.amount} />
                                                </span>
                                            </div>
                                        </div>
                                        <div className='col-6'>
                                            <div className='ss-receipt-card-field'>
                                                <span className='ss-receipt-card-field__label'>Uploaded Date</span>
                                                <span className='ss-receipt-card-field__value'>
                                                    <DateTimeComp formattedDate={item.createdDT} />
                                                </span>
                                            </div>
                                        </div>
                                        <div className='col-6'>
                                            <div className='ss-receipt-card-field'>
                                                <span className='ss-receipt-card-field__label'>Uploaded From</span>
                                                <span className='ss-receipt-card-field__value d-inline-flex align-items-center gap-1'>
                                                    <SourceIcon recordSourceId={item.recordSourceID ?? item.recordSourceId} />
                                                </span>
                                            </div>
                                        </div>
                                        <div className='col-12'>
                                            <div className='ss-receipt-card-field mt-1'>
                                                <span className='ss-receipt-card-field__label'>Description</span>
                                                <div className='ss-receipt-card-desc-box' title={item.readDocShortDesc || ''}>
                                                    {item.readDocShortDesc || 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {showFooterActions && (
                                <div className="card-footer ss-receipt-grid-card__footer py-2 px-3" onClick={(e) => e.stopPropagation()}>
                                    <div className="d-flex flex-wrap gap-1 align-items-center">
                                        {item?.ocrReadStatus === 2 && item?.noOfRetry <= 2 && (
                                            <button type="button" className='btn btn-sm btn-warning' onClick={() => onRetry(item?.invoiceDataDraftId)}>
                                                Retry ({item?.noOfRetry})
                                            </button>
                                        )}
                                        {(item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4) && item.trackingId && onViewReport && (
                                            <button type="button" className='btn btn-sm btn-primary' onClick={() => onViewReport(item)}>
                                                View Report
                                            </button>
                                        )}
                                        {(item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4) && lineItemId && onViewLineItem && (
                                            <button type="button" className='btn btn-sm btn-primary' onClick={() => onViewLineItem(lineItemId)}>
                                                View Line Item
                                            </button>
                                        )}
                                        {item.ocrReadStatus === 5 && lineItemId && onViewLineItem && (
                                            <button type="button" className='btn btn-sm btn-primary' onClick={() => onViewLineItem(lineItemId)}>
                                                View Used Line Item
                                            </button>
                                        )}
                                        {lineItemId && onCompareOcr && (
                                            <button type="button" className='btn btn-sm btn-light-info' onClick={() => onCompareOcr(item)}>
                                                Compare OCR
                                            </button>
                                        )}
                                        {item?.fileURL && onCopyAttachmentUrl && (
                                            <button type="button" className='btn btn-sm btn-light-primary' onClick={() => onCopyAttachmentUrl(item)}>
                                                Copy URL
                                            </button>
                                        )}
                                        {canViewViolations(item) && onViewViolations && (
                                            <button type="button" className='btn btn-sm btn-light-danger' onClick={() => onViewViolations(item)}>
                                                View Violations
                                            </button>
                                        )}
                                        {canEditLineItem(item) && onEditLineItem && (
                                            <button type="button" className='btn btn-sm btn-light-info' onClick={() => onEditLineItem(item)}>
                                                Edit LineItem
                                            </button>
                                        )}
                                        {canMergeLineItem(item) && onMergeLineItem && (
                                            <button type="button" className='btn btn-sm btn-light-success' onClick={() => onMergeLineItem(item)}>
                                                Merge Line Item
                                            </button>
                                        )}
                                        {canUnmergeLineItem(item) && onUnmergeLineItem && (
                                            <button type="button" className='btn btn-sm btn-light-danger' onClick={() => onUnmergeLineItem(item)}>
                                                UnMerge LineItem
                                            </button>
                                        )}
                                        {onViewOcrText && (
                                            <button type="button" className='btn btn-sm btn-light' onClick={() => onViewOcrText(item)}>
                                                View OCR Text
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default InvoiceGridList;